import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScopeRouterStore } from "../store/scopeRouterStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * Scope Router (v2)
 * 
 * UI for managing scope categorization, article references,
 * routing paths, and cross-border bonuses.
 */
export default function ScopeRouterV2() {
  const { t } = useTranslation();
  const {
    scopeCategory,
    articleRef,
    supplyChainCount,
    setScopeCategory,
    setArticleRef,
    setSupplyChainCount,
    exportScopeRoute,
  } = useScopeRouterStore();

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("Scope Router");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("scopeCategory", scopeCategory);
    useAIContext.getState().setInput("articleRef", articleRef);
    useAIContext.getState().setInput("supplyChainCount", supplyChainCount);
  }, [scopeCategory, articleRef, supplyChainCount]);

  const routingPath = useScopeRouterStore((s) => s.getRoutingPath());
  const ledgerTarget = useScopeRouterStore((s) => s.getLedgerTarget());
  const crossBorderBonus = useScopeRouterStore((s) => s.getCrossBorderBonus());
  const articleMismatch = useScopeRouterStore((s) => s.getArticleMismatch());

  // Loading and error states
  const [error, setError] = useState<Error | null>(null);

  const handleExport = () => {
    setError(null);
    try {
      exportScopeRoute();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("scopeRouter.exportFailed") || "Failed to export scope route");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100 p-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("scopeRouter.title") || "Scope Router"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeRouter.scopeCategory") || "Scope Category"}
          </label>
          <select
            value={scopeCategory}
            onChange={(e) => setScopeCategory(e.target.value as any)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          >
            <option value="scope1">Scope 1</option>
            <option value="scope2">Scope 2</option>
            <option value="scope3">Scope 3</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeRouter.articleRef") || "Article Reference"}
          </label>
          <select
            value={articleRef}
            onChange={(e) => setArticleRef(e.target.value as any)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          >
            <option value="A8">A8</option>
            <option value="A9">A9</option>
            <option value="A10">A10</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeRouter.supplyChainCount") || "Supply Chain Count"}
          </label>
          <input
            type="number"
            min="0"
            value={supplyChainCount}
            onChange={(e) => setSupplyChainCount(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      {/* Derived Metrics Display */}
      <div className="space-y-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="text-sm text-neutral-400 mb-2">
            {t("scopeRouter.routingPath") || "Routing Path"}
          </div>
          <div className="font-mono text-xs text-evat-neon space-y-1">
            {routingPath.map((path, idx) => (
              <div key={idx}>{path}</div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
            <div className="text-xs text-neutral-400 mb-1">
              {t("scopeRouter.ledgerTarget") || "Ledger Target"}
            </div>
            <div className="text-sm font-orbitron text-evat-gold">
              {ledgerTarget}
            </div>
          </div>

          <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
            <div className="text-xs text-neutral-400 mb-1">
              {t("scopeRouter.crossBorderBonus") || "Cross-Border Bonus"}
            </div>
            <div className="text-sm font-orbitron text-evat-neon">
              +{crossBorderBonus.toFixed(2)}
            </div>
          </div>
        </div>

        {articleMismatch && (
          <div className="border border-red-500/40 rounded p-3 bg-red-900/20">
            <div className="text-sm text-red-300">
              ⚠️ {t("scopeRouter.articleMismatch") || "Warning: Article mismatch detected for this scope category!"}
            </div>
          </div>
        )}
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("scopeRouter.export") || "Export JSON report"}
        >
          {t("scopeRouter.export") || "Export JSON"}
        </button>
      </div>
    </div>
  );
}




